import React, { useEffect, useMemo, useState } from "react";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { Search, MoreVertical, X, Copy, Loader2, Loader } from "lucide-react";
import { toast } from "react-hot-toast";
import Dropdown from "../../../components/ui/Dropdown";
import Pagination from "../../../components/ui/Pagination";
import DeclineReasonModal from "../../../components/ui/DeclineReasonModal";
import SuccessNoteModal from "../../../components/ui/SuccessNoteModal";
import {
  useMerchantPayouts,
  usePayoutPartnersList,
  useRetryMerchantPayout,
  useDeclineMerchantPayout,
  useMarkMerchantPayoutSuccessful,
} from "../hooks/useMerchantPayouts";

const STATUSES = [
  { value: "", label: "All statuses" },
  { value: "PENDING", label: "Pending" },
  { value: "PROCESSING", label: "Processing" },
  { value: "SUCCESS", label: "Successful" },
  { value: "FAILED", label: "Failed" },
];

const STATUS_STYLES = {
  PENDING: "bg-yellow-100 text-yellow-800",
  PROCESSING: "bg-blue-100 text-blue-800",
  SUCCESS: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
};

// only a payout still in flight can be changed
const isOpen = (payout) => payout.status === "PENDING" || payout.status === "PROCESSING";

const money = (value) => Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const failureText = (failure) => {
  if (!failure) return "";
  if (typeof failure === "string") return failure;
  return failure.message || failure.reason || failure.error || JSON.stringify(failure);
};

const ACTION_LABELS = { retry: "Re-checked with partner", decline: "Declined", mark_successful: "Marked successful" };

const DetailsModal = ({ payout, onClose }) => {
  if (!payout) return null;
  const item = (label, value) => (
    <div className="flex flex-col mb-4">
      <span className="text-xs tracking-wider text-gray-500 uppercase">{label}</span>
      <span className="text-sm font-medium text-gray-900 break-all">{value || "N/A"}</span>
    </div>
  );
  const code = (payout.currency || "").toUpperCase();
  return (
    <div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
      <div className="overflow-hidden mx-4 w-full max-w-2xl max-h-[90vh] bg-white rounded-2xl shadow-2xl">
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Payout details</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[75vh]">
          <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2">
            {item("Payout ID", payout.uuid)}
            {item("Reference", payout.reference)}
            {item("Status", payout.status)}
            {item("Date", payout.createdAt ? new Date(payout.createdAt).toLocaleString() : "")}
            {item("Processed by (partner)", payout.partner)}
            {item("Partner reference", payout.providerReference)}
            {item("Merchant", payout.merchant?.name)}
            {item("Merchant email", payout.merchant?.email)}
            {item("Recipient", payout.beneficiary?.name)}
            {item("Recipient country", payout.beneficiary?.countryCode)}
            {item("Payout method", payout.payoutMethod)}
            {item("Amount", `${code} ${money(payout.amount)}`)}
            {item("Total charged to merchant", `${code} ${money(payout.totalAmount)}`)}
            {item("Partner fee", `${code} ${money(payout.providerFee)}`)}
            {item("Platform fee", `${code} ${money(payout.platformFee)}`)}
            {item("Network fee", `${code} ${money(payout.networkFee)}`)}
          </div>
          {item("Description", payout.description)}
          {payout.failure && (
            <div className="p-3 mb-4 text-sm text-red-700 bg-red-50 rounded-lg border border-red-100">
              <span className="block mb-1 text-xs font-bold uppercase">Partner or system message</span>
              {failureText(payout.failure)}
            </div>
          )}
          <h4 className="pb-2 mt-2 mb-3 text-sm font-bold tracking-wide text-emerald-800 uppercase border-b border-emerald-100">
            Manual actions
          </h4>
          {payout.adminActions?.length ? (
            <ul className="space-y-3">
              {payout.adminActions.map((a, i) => (
                <li key={i} className="text-sm text-gray-700">
                  <span className="font-semibold">{ACTION_LABELS[a.action] || a.action}</span> by{" "}
                  {a.adminEmail || `admin ${a.adminId ?? "unknown"}`}, {a.from} to {a.to},{" "}
                  {a.at ? new Date(a.at).toLocaleString() : ""}
                  {a.reason ? <span className="block text-gray-500">Reason: {a.reason}</span> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">None yet.</p>
          )}
        </div>
        <div className="flex justify-end px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-bold text-gray-600 bg-white rounded-xl border-2 border-gray-200 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// the shared modals show a consumer-shaped summary: give them this payout in that shape
const forModal = (p) =>
  p && {
    reference: p.reference,
    paymentGatewayReference: p.providerReference,
    user: { firstName: p.merchant?.name, lastName: "" },
    currency: { symbol: (p.currency || "").toUpperCase() },
    amount: p.amount,
  };

export function MerchantPayoutTable() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [provider, setProvider] = useState("");
  const [currency, setCurrency] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const [details, setDetails] = useState(null);
  const [declineTarget, setDeclineTarget] = useState(null);
  const [successTarget, setSuccessTarget] = useState(null);
  const [loadingRowId, setLoadingRowId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const params = {
    page,
    limit,
    search: debouncedSearch,
    status,
    provider,
    currency: currency.trim(),
    from: from ? new Date(from).toISOString() : "",
    to: to ? new Date(`${to}T23:59:59`).toISOString() : "",
  };
  const { data, isLoading, isError, error, isFetching } = useMerchantPayouts(params);
  const { data: partners } = usePayoutPartnersList();
  const { mutate: retryPayout } = useRetryMerchantPayout();
  const { mutate: declinePayout, isPending: isDeclining } = useDeclineMerchantPayout();
  const { mutate: markSuccessful, isPending: isMarking } = useMarkMerchantPayoutSuccessful();

  const rows = useMemo(() => data?.data ?? [], [data]);
  const totalPages = data?.pagination?.totalPages || 1;
  const total = data?.pagination?.total ?? 0;

  const changeFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const short = (text, n = 12) => (text && text.length > n ? `${text.slice(0, n)}...` : text || "---");

  const handleRetry = (payout) => {
    setLoadingRowId(payout.uuid);
    retryPayout(payout.uuid, {
      onSuccess: (res) => {
        // a payout the partner is still working on is not an error, and not a change
        (res?.changed ? toast.success : toast)(res?.message || "Checked with the partner");
        setLoadingRowId(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Could not check this payout");
        setLoadingRowId(null);
      },
    });
  };

  const handleDecline = (reason) => {
    const target = declineTarget;
    setLoadingRowId(target.uuid);
    declinePayout(
      { uuid: target.uuid, reason },
      {
        onSuccess: (res) => {
          toast.success(res?.message || "Payout declined");
          setDeclineTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Could not decline this payout");
          setLoadingRowId(null);
        },
      }
    );
  };

  const handleMarkSuccessful = ({ note, providerReference }) => {
    const target = successTarget;
    setLoadingRowId(target.uuid);
    markSuccessful(
      { uuid: target.uuid, reason: note, providerReference },
      {
        onSuccess: (res) => {
          toast.success(res?.message || "Payout marked successful");
          setSuccessTarget(null);
          setLoadingRowId(null);
        },
        onError: (err) => {
          toast.error(err?.response?.data?.message || "Could not mark this payout successful");
          setLoadingRowId(null);
        },
      }
    );
  };

  const columns = [
    {
      id: "date",
      header: "Date",
      cell: ({ row }) => (
        <span className="whitespace-nowrap">
          {row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : "N/A"}
        </span>
      ),
    },
    {
      id: "reference",
      header: "Reference",
      cell: ({ row }) => (
        <div className="flex gap-1 justify-between items-center min-w-[140px]">
          <span title={row.original.reference}>{short(row.original.reference, 14)}</span>
          <button onClick={() => copy(row.original.reference)} className="text-gray-500 hover:text-black">
            <Copy size={14} />
          </button>
        </div>
      ),
    },
    {
      id: "merchant",
      header: "Merchant",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium text-gray-900">{row.original.merchant?.name || "N/A"}</span>
          <span className="text-xs text-gray-500">{row.original.merchant?.email}</span>
        </div>
      ),
    },
    {
      id: "recipient",
      header: "Recipient",
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span>{row.original.beneficiary?.name || "N/A"}</span>
          <span className="text-xs text-gray-500">
            {[row.original.beneficiary?.countryCode, row.original.payoutMethod].filter(Boolean).join(" · ")}
          </span>
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      cell: ({ row }) => (
        <span className="font-semibold whitespace-nowrap">
          {(row.original.currency || "").toUpperCase()} {money(row.original.amount)}
        </span>
      ),
    },
    {
      id: "partner",
      header: "Processed by",
      cell: ({ row }) =>
        row.original.partner ? (
          <div className="flex flex-col">
            <span className="px-3 py-1 w-fit text-xs font-medium text-emerald-800 capitalize bg-emerald-50 rounded-full">
              {row.original.partner}
            </span>
            <span className="mt-1 text-xs text-gray-500" title={row.original.providerReference || ""}>
              {row.original.providerReference ? short(row.original.providerReference, 14) : "no partner reference"}
            </span>
          </div>
        ) : (
          <span className="text-xs text-gray-400">Not assigned</span>
        ),
    },
    {
      id: "status",
      header: "Status",
      cell: ({ row }) => (
        <span
          className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
            STATUS_STYLES[row.original.status] || "bg-gray-100 text-gray-800"
          }`}
        >
          {row.original.status === "SUCCESS" ? "SUCCESSFUL" : row.original.status}
        </span>
      ),
    },
    {
      id: "note",
      header: "Message",
      cell: ({ row }) => {
        const text = failureText(row.original.failure);
        const last = row.original.adminActions?.[row.original.adminActions.length - 1];
        return (
          <div className="flex flex-col max-w-[220px]">
            {text ? <span className="text-xs text-red-600 line-clamp-2" title={text}>{text}</span> : null}
            {last ? (
              <span className="text-xs text-gray-500">
                {ACTION_LABELS[last.action] || last.action} by {last.adminEmail || "admin"}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const payout = row.original;
        const busy = loadingRowId === payout.uuid;
        const items = [{ label: "View details", onClick: () => setDetails(payout) }];
        if (isOpen(payout)) {
          items.push(
            { label: busy ? "Checking..." : "Retry (check with partner)", onClick: () => handleRetry(payout), className: "text-emerald-600" },
            { label: "Mark successful", onClick: () => setSuccessTarget(payout), className: "text-emerald-600" },
            { label: "Decline and refund", onClick: () => setDeclineTarget(payout), className: "text-red-600" }
          );
        }
        return (
          <Dropdown
            trigger={busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
            items={items}
          />
        );
      },
    },
  ];

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  if (isError) {
    return (
      <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
        Error: {error?.response?.data?.message || error?.message}
      </p>
    );
  }

  const selectClass =
    "py-3 px-4 text-sm bg-white rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500";

  return (
    <div className="min-h-screen">
      <div className="flex flex-wrap gap-3 items-center pb-5">
        <div className="relative w-80">
          <Search className="absolute left-4 top-1/2 w-5 h-5 text-gray-400 transform -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search reference, merchant or recipient..."
            className="py-3 pr-4 pl-12 w-full text-sm rounded-3xl border border-gray-200 outline-none focus:ring-2 focus:ring-emerald-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select aria-label="Status" className={selectClass} value={status} onChange={changeFilter(setStatus)}>
          {STATUSES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <select aria-label="Payout partner" className={selectClass} value={provider} onChange={changeFilter(setProvider)}>
          <option value="">All partners</option>
          {(partners || []).map((p) => (
            <option key={p.uuid || p.name} value={p.name}>{p.name}</option>
          ))}
        </select>
        <input
          aria-label="Currency"
          placeholder="Currency (e.g. INR)"
          className={`${selectClass} w-40`}
          value={currency}
          onChange={changeFilter(setCurrency)}
        />
        <label className="flex gap-2 items-center text-xs text-gray-500">
          From
          <input type="date" className={selectClass} value={from} onChange={changeFilter(setFrom)} />
        </label>
        <label className="flex gap-2 items-center text-xs text-gray-500">
          To
          <input type="date" className={selectClass} value={to} onChange={changeFilter(setTo)} />
        </label>
        <span className="ml-auto text-sm text-gray-500">
          {isFetching ? "Updating..." : `${total.toLocaleString()} payout${total === 1 ? "" : "s"}`}
        </span>
      </div>

      {isLoading ? (
        <div className="flex h-[400px] justify-center items-center">
          <p className="flex gap-2 items-center font-medium text-gray-600">
            Loading merchant payouts <Loader className="w-5 h-5 animate-spin" />
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="bg-gray-50/50">
                    {hg.headers.map((header) => (
                      <th key={header.id} className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:px-6">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <p className="text-lg font-medium">No payouts match these filters</p>
                      <p className="text-sm">Try a different status, partner or date range</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      )}

      <DetailsModal payout={details} onClose={() => setDetails(null)} />

      <DeclineReasonModal
        isOpen={!!declineTarget}
        onClose={() => setDeclineTarget(null)}
        onSubmit={handleDecline}
        title="Decline and refund this payout"
        isLoading={isDeclining}
        data={forModal(declineTarget)}
      />

      <SuccessNoteModal
        isOpen={!!successTarget}
        onClose={() => setSuccessTarget(null)}
        onSubmit={handleMarkSuccessful}
        title="Mark this payout successful"
        isLoading={isMarking}
        data={forModal(successTarget)}
      />
    </div>
  );
}
